import * as React from 'react';
import { usePostHog } from '@posthog/react';
import { Theme } from '@radix-ui/themes';
import '@radix-ui/themes/styles.css';
import * as Sentry from '@sentry/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type PostHog } from 'posthog-js';
import {
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  type ShouldRevalidateFunctionArgs,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from 'react-router';

import { useInitializeFlaskApi } from '~/auth/flask-api';
import { dataWithCookie } from '~/auth/flask-api.server';
import { CartDrawer } from '~/components/Cart/CartDrawer';
import { DrawerProvider } from '~/components/Cart/CartDrawerContext';
import { ErrorPage } from '~/components/ErrorPage';
import { Navbar } from '~/components/Navbar';
import { PostHogAppProvider } from '~/components/PostHogAppProvider';
import { Toaster } from '~/components/ui/toaster';
import '~/index.css';
import { getRootLoaderData } from '~/middleware/getRootLoaderData';
import type { RootLoaderData } from '~/types/root';
import { MaintenanceMode, env } from '~/utils/env.server';

import GlobalLoader from './components/Loader/GlobalLoader';
import { TooltipProvider } from './components/ui/tooltip';
import { paths } from './constants/paths';
import { useDetectInactivity } from './hooks/useDetectInactivity';
import { cn } from './lib/utils';

function AnalyticsTracker({
  user,
  userName,
  userEmail,
  userRole,
  isSupplier,
  isConduitEmployee,
  organizationId,
  organizationName,
}: {
  user: RootLoaderData['user'];
  userName: string;
  userEmail: string;
  userRole: string;
  isSupplier: boolean;
  isConduitEmployee: boolean;
  organizationId: string | undefined;
  organizationName: string | undefined;
}) {
  const posthog = usePostHog() as PostHog | undefined;
  const { pathname } = useLocation();
  const identityReady = React.useRef(false);
  const prevPathname = React.useRef(pathname);

  React.useEffect(() => {
    if (user) {
      posthog?.identify(user.id, {
        email: userEmail || undefined,
        name: userName,
        role: userRole,
        is_supplier: isSupplier,
        is_conduit_employee: isConduitEmployee,
      });

      Sentry.setUser({
        id: user.id,
        username: userName,
        email: userEmail || undefined,
        role: userRole,
        is_conduit_employee: isConduitEmployee,
      });

      if (organizationId) {
        posthog?.group('organization', organizationId, {
          name: organizationName,
        });
      }
    } else {
      posthog?.reset();
      Sentry.setUser(null);
    }

    if (!identityReady.current) {
      posthog?.capture('$pageview');
      identityReady.current = true;
    }
  }, [
    user,
    userEmail,
    userName,
    userRole,
    isSupplier,
    isConduitEmployee,
    organizationId,
    organizationName,
    posthog,
  ]);

  React.useEffect(() => {
    if (prevPathname.current === pathname) return;
    prevPathname.current = pathname;
    posthog?.capture('$pageview');
  }, [pathname, posthog]);

  return null;
}

const DevMockMenu = React.lazy(() =>
  import('./components/dev-mock-menu/DevMockMenu').then((m) => ({ default: m.DevMockMenu }))
);

function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <React.Suspense>{children}</React.Suspense>;
}

export const links: LinksFunction = () => {
  return [];
};

export const meta: MetaFunction = () => {
  return [{ title: 'Conduit Health' }];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const rootLoaderData = await getRootLoaderData(request);
  const { cookieHeader, ...loaderData } = rootLoaderData;
  const { ENVIRONMENT, GRAPHQL_ENDPOINT, MAINTENANCE, SENTRY_DSN } = env;
  const isMaintenanceModeEnabled =
    MAINTENANCE === MaintenanceMode.Enabled || request.url.includes(`/?%21`);

  return dataWithCookie(
    {
      ...loaderData,
      SENTRY_DSN,
      environment: ENVIRONMENT,
      isMaintenanceModeEnabled,
      graphqlEndpoint: GRAPHQL_ENDPOINT,
    } satisfies RootLoaderData,
    cookieHeader
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root');
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="font-inter overflow-hidden">
        <Theme>
          {children}
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(data?.ENV)}`,
            }}
          />
          <Scripts />
          <ScrollRestoration />
        </Theme>
      </body>
    </html>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (error: any) => {
        console.error('TanStack Query Error:', error);
        return false;
      },
    },
  },
});

export default function App() {
  const {
    user,
    userRole,
    userName,
    userEmail,
    environment,
    isMaintenanceModeEnabled,
    isJoinOrganizationPending,
    isImpersonating,
    isSupplier,
    isConduitEmployee,
    organizationId,
    organizationName,
    ENV,
  } = useLoaderData<typeof loader>();
  const mocksEnabled = ENV.VITE_ENABLE_MOCKS === 'true';

  useInitializeFlaskApi();
  useDetectInactivity();

  React.useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        username: userName,
        email: userEmail || undefined,
        role: userRole,
        is_conduit_employee: isConduitEmployee,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user, userName, userEmail, userRole, isConduitEmployee]);

  const navbarProps = {
    user,
    userRole,
    userName,
    environment,
    isImpersonating,
  };

  const { pathname } = useLocation();

  React.useEffect(() => {
    document.getElementById('rootDiv')?.scrollTo(0, 0);
  }, [pathname]);

  const mainRoot = (
    <>
      {isMaintenanceModeEnabled ? (
        <ErrorPage type="Maintenance" className="pt-20" />
      ) : isJoinOrganizationPending ? (
        <>
          <Navbar {...navbarProps} />
          <ErrorPage type="Pending" className="pt-20" />
        </>
      ) : (
        <>
          <Navbar {...navbarProps} />
          <div id="rootDiv" className="min-h-0 flex-1 overflow-auto">
            <Outlet />
          </div>
          <GlobalLoader />
        </>
      )}
      <Toaster />
      {mocksEnabled ? (
        <ClientOnly>
          <DevMockMenu />
        </ClientOnly>
      ) : null}
    </>
  );

  const content = (
    <PostHogAppProvider>
      <QueryClientProvider client={queryClient}>
        <AnalyticsTracker
          user={user}
          userName={userName}
          userEmail={userEmail}
          userRole={userRole}
          isSupplier={isSupplier}
          isConduitEmployee={isConduitEmployee}
          organizationId={organizationId}
          organizationName={organizationName}
        />
        <TooltipProvider>
          <DrawerProvider>
            {mainRoot}
            <CartDrawer />
          </DrawerProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </PostHogAppProvider>
  );

  return (
    <div className={cn(!isMaintenanceModeEnabled && 'flex h-dvh flex-col overflow-hidden')}>
      {content}
    </div>
  );
}

export function ErrorBoundary() {
  const posthog = usePostHog();
  const error = useRouteError();
  const rootData = useRouteLoaderData<typeof loader>('root');

  // Only capture non-404 and non-401 errors
  if (!isRouteErrorResponse(error) || (error.status !== 404 && error.status !== 401)) {
    Sentry.captureException(error);
    posthog?.captureException(error);
  }

  // Redirect to login on auth failures. This is a safety net for uncaught auth failures.
  // Only use /app paths as returnTo - using auth routes (e.g. /auth/auth0) causes
  // infinite redirect loops when the root loader throws 401 with stale cookies.
  const shouldRedirectToLogin =
    isRouteErrorResponse(error) &&
    (error.status === 401 || (error.status === 500 && rootData?.user == null));

  if (shouldRedirectToLogin) {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : paths.app;
    const returnTo = currentPath.startsWith(paths.app) ? currentPath : undefined;
    const loginUrl = paths.login(returnTo);

    if (typeof window !== 'undefined') {
      window.location.href = loginUrl;
      return null;
    }

    return (
      <div className="flex h-dvh items-center justify-center">
        <p>
          Session expired.{' '}
          <a href={loginUrl} className="text-blue-600 underline">
            Click here to log in
          </a>
        </p>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.location.href = ${JSON.stringify(loginUrl)};`,
          }}
        />
      </div>
    );
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <ErrorPage type="Not_Found" />
      </div>
    );
  }

  return (
    <div className="flex h-dvh items-center justify-center">
      <ErrorPage type="Unexpected" />
    </div>
  );
}

export function shouldRevalidate({
  defaultShouldRevalidate,
  formAction,
}: ShouldRevalidateFunctionArgs) {
  // endpoints that should trigger a revalidation
  const endpoints = [
    '/clear-notification',
    '/fetch-notifications',
    '/read-notification',
    paths.marketplaceDiscover,
    '/auth/auth0',
    paths.logout,
    '/auth/impersonate',
    '/auth/callback',
    paths.profile,
    paths.profileMemberRequests,
    paths.profileNotificationPreferences,
    '/get-new-session',
  ];

  const checkoutEndpoint = paths.checkout;

  if (formAction && (endpoints.includes(formAction) || formAction.includes(checkoutEndpoint))) {
    return defaultShouldRevalidate;
  }

  return false;
}
